/**
 * Height reservation for draw.io diagrams, shared by getCalculatedHeightFromNode.ts (renderer) and
 * useExtensionNodeParams.ts (editor).
 *
 * Any changes here should be mirrored in content-renderer-legacy-macros/src/useConnectExtensionViewportSize.ts
 */
export const DRAW_IO_HEIGHT_OFFSET = 52;

/**
 * Extension nodes in the editor can range between 556px and 760px wide depending on the browser width.
 * For smaller viewport sizes (i.e. 500px), Draw.io will resize the height of the content based on its
 * ratio for widths above 530px, so the stored height of wider diagrams can't be trusted.
 */
const DRAW_IO_MAX_UNRESIZED_WIDTH = 530;

const CONNECT_DRAW_IO_EXTENSION_KEYS = new Set(['drawio', 'inc-drawio', 'drawio-sketch']);

// Forge draw.io extension keys are `<appId>/<environmentId>/static/drawio`
const FORGE_DRAW_IO_EXTENSION_KEY_SUFFIX = '/static/drawio';

// Forge draw.io may store its zoom as a factor (1) or a percentage (100); both mean native size
const GUEST_PARAMS_NATIVE_ZOOMS = [1, 100];

type DrawioParameters =
	| {
			guestParams?: Record<string, unknown>;
			macroParams?: Record<string, { value?: unknown } | undefined>;
	  }
	| undefined;

/**
 * Callsites check this before evaluating the confluence_preset_height_migrated_drawio experiment
 * inline, so the experiment is only evaluated (and exposure logged) for draw.io keys.
 */
export const isDrawioExtensionKey = (extensionKey: unknown): boolean =>
	typeof extensionKey === 'string' &&
	(CONNECT_DRAW_IO_EXTENSION_KEYS.has(extensionKey) ||
		extensionKey.endsWith(FORGE_DRAW_IO_EXTENSION_KEY_SUFFIX));

const isNumeric = (value: unknown): boolean =>
	value !== null && value !== undefined && value !== '' && !Number.isNaN(Number(value));

const isResizedByDrawio = (width: unknown): boolean => {
	const diagramWidth = Number(width);
	return Boolean(diagramWidth) && diagramWidth > DRAW_IO_MAX_UNRESIZED_WIDTH;
};

/**
 * Returns the natural diagram height to reserve (without DRAW_IO_HEIGHT_OFFSET), or undefined when it
 * can't be trusted.
 *
 * 1. Forge `guestParams` are checked first. Diagrams migrated from Connect keep their original Connect
 *    `macroParams`, which can be stale, alongside the current Forge `guestParams`.
 * 2. Otherwise fall back to the Connect `macroParams`.
 *
 * Diagrams that aren't at native zoom, or that are wide enough for Draw.io to resize, are excluded
 * because their rendered height differs from the stored height.
 */
export const getDrawioHeight = (parameters: DrawioParameters): unknown => {
	const guestParams = parameters?.guestParams;
	const macroParams = parameters?.macroParams;

	if (
		GUEST_PARAMS_NATIVE_ZOOMS.includes(Number(guestParams?.zoom)) &&
		isNumeric(guestParams?.height)
	) {
		return isResizedByDrawio(guestParams?.width) ? undefined : guestParams?.height;
	}

	if (Number(macroParams?.zoom?.value) === 1) {
		return isResizedByDrawio(macroParams?.width?.value) ? undefined : macroParams?.height?.value;
	}

	return undefined;
};
